import {
  Container,
  Typography,
  Grid,
  Box,
  Divider,
  Card,
  CardContent,
  Chip,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import Hero from '../components/common/Hero';
import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EventIcon from '@mui/icons-material/Event';
import ReactMarkdown from 'react-markdown';
import { eventService, getStrapiMediaUrl } from '../services/api';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  // Get current date for reference
  // eslint-disable-next-line no-unused-vars
  const currentDate = new Date();

  // Get month name
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const filterOptions = [
    { value: 'all', label: 'All Events' },
    { value: 'upcoming', label: 'Upcoming Events' },
    { value: 'past', label: 'Past Events' },
    { value: 'tba', label: 'TBA Events' }
  ];

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await eventService.getAll({
          populate: '*',
          sort: ['startDate:asc']
        });

        if (response && response.data) {
          // Transform Strapi response to match our component's expected format
          const formattedEvents = response.data.map(item => {
            const evt = item.attributes || item;
            const rawStartDate = evt.startDate ? new Date(evt.startDate) : null;
            const rawEndDate = evt.endDate ? new Date(evt.endDate) : null;

            return {
              id: item.id,
              title: evt.title || (evt.Title) || '',
              rawDate: rawStartDate,
              startDate: evt.isTBA ? 'TBA' : rawStartDate ? rawStartDate.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric'
              }) : 'TBA',
              endDate: rawEndDate ? rawEndDate.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric'
              }) : null,
              location: evt.location || '',
              address: evt.address || '',
              content: evt.content || '',
              category: evt.category || 'social',
              isTBA: evt.isTBA || false,
              isRecurring: evt.isRecurring || false,
              recurrencePattern: evt.recurrencePattern || '',
              isPast: rawStartDate ? rawStartDate < new Date() : false,
              isUpcoming: rawStartDate ? rawStartDate >= new Date() : true,
              featuredImage: evt.featuredImage?.data ? getStrapiMediaUrl(evt.featuredImage.data.attributes.url) : null,
            };
          });

          setEvents(formattedEvents);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching events:', err);
        setError('Failed to load events. Please try again later.');
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
  };

  const getFilteredEvents = () => {
    switch (filter) {
      case 'upcoming':
        return events.filter(event => event.isUpcoming && !event.isTBA);
      case 'past':
        return events.filter(event => event.isPast);
      case 'tba':
        return events.filter(event => event.isTBA);
      default:
        return events;
    }
  };

  // Group events by month and year
  const groupEventsByMonth = () => {
    const filteredEvents = getFilteredEvents();
    const groupedEvents = {};

    filteredEvents.forEach(event => {
      if (event.isTBA) {
        if (!groupedEvents['TBA']) {
          groupedEvents['TBA'] = [];
        }
        groupedEvents['TBA'].push(event);
      } else if (event.rawDate) {
        const month = event.rawDate.getMonth();
        const year = event.rawDate.getFullYear();
        const key = `${year}-${month}`;

        if (!groupedEvents[key]) {
          groupedEvents[key] = {
            month,
            year,
            events: []
          };
        }

        groupedEvents[key].events.push(event);
      }
    });

    return groupedEvents;
  };

  return (
    <>
      <Helmet>
        <title>Community Events Calendar | Brynleigh Estates HOA</title>
        <meta name="description" content="Stay updated with upcoming community events, meetings, and gatherings at Brynleigh Estates in Chelsea, Alabama. View our community calendar." />
        <link rel="canonical" href="https://brynleighestates.com/events" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://brynleighestates.com/events" />
        <meta property="og:title" content="Community Events Calendar | Brynleigh Estates HOA" />
        <meta property="og:description" content="Stay updated with upcoming community events, meetings, and gatherings at Brynleigh Estates in Chelsea, Alabama." />
        <meta property="og:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Community Events Calendar | Brynleigh Estates HOA" />
        <meta name="twitter:description" content="Stay updated with upcoming community events, meetings, and gatherings at Brynleigh Estates." />
        <meta name="twitter:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
      </Helmet>

      <Hero
        title="Community Events Calendar"
        subtitle="Stay informed about upcoming community gatherings and important dates"
        imageUrl="/images/hills-bg.jpeg"
      />

      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" gutterBottom align="center">
            Events Calendar
          </Typography>
          <Divider sx={{ mb: 4 }} />

          {/* Filter */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <TextField
              select
              label="Filter Events"
              value={filter}
              onChange={handleFilterChange}
              variant="outlined"
              size="small"
              sx={{ width: { xs: '100%', sm: '240px' } }}
            >
              {filterOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ my: 4 }}>{error}</Alert>
          ) : events.length === 0 ? (
            <Alert severity="info" sx={{ my: 4 }}>No events available at this time.</Alert>
          ) : (
            <Box>
              {Object.entries(groupEventsByMonth())
                .sort(([keyA, dataA], [keyB, dataB]) => {
                  // TBA events first
                  if (keyA === 'TBA') return -1;
                  if (keyB === 'TBA') return 1;

                  const now = new Date();
                  const currentYearMonth = now.getFullYear() * 12 + now.getMonth();
                  const yearMonthA = dataA.year * 12 + dataA.month;
                  const yearMonthB = dataB.year * 12 + dataB.month;
                  const isFutureA = yearMonthA >= currentYearMonth;
                  const isFutureB = yearMonthB >= currentYearMonth;

                  // Future events before past events
                  if (isFutureA && !isFutureB) return -1;
                  if (!isFutureA && isFutureB) return 1;

                  // Within same category: future ascending, past descending
                  if (isFutureA) return yearMonthA - yearMonthB;
                  return yearMonthB - yearMonthA;
                })
                .map(([key, monthData]) => (
                  <Box key={key} sx={{ mb: 6 }}>
                    <Paper
                      elevation={2}
                      sx={{
                        p: 2,
                        mb: 2,
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderRadius: 2
                      }}
                    >
                      <Typography variant="h5" component="h3">
                        {key === 'TBA' ? 'Date To Be Announced' : `${monthNames[monthData.month]} ${monthData.year}`}
                      </Typography>
                    </Paper>

                    <Grid container spacing={3}>
                      {(key === 'TBA' ? monthData : monthData.events).map((event, index) => (
                        <Grid item xs={12} md={6} key={event.id}>
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                          >
                            <Card
                              elevation={3}
                              sx={{
                                height: '100%',
                                borderRadius: 2,
                                transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                                '&:hover': {
                                  transform: 'translateY(-5px)',
                                  boxShadow: 6
                                }
                              }}
                            >
                              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box>
                                  <Typography variant="h6" component="h4" gutterBottom>
                                    {event.title}
                                    {event.isUpcoming && !event.isTBA && (
                                      <Chip
                                        label="Upcoming"
                                        size="small"
                                        color="primary"
                                        sx={{ ml: 1, verticalAlign: 'middle' }}
                                      />
                                    )}
                                    {event.isTBA && (
                                      <Chip
                                        label="TBA"
                                        size="small"
                                        color="secondary"
                                        sx={{ ml: 1, verticalAlign: 'middle' }}
                                      />
                                    )}
                                  </Typography>

                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="text.secondary">
                                      {event.startDate}
                                    </Typography>
                                  </Box>

                                  {event.location && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                      <LocationOnIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                      <Typography variant="body2" color="text.secondary">
                                        {event.location}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>

                                {event.content && (
                                  <Box
                                    sx={{
                                      flexGrow: 1,
                                      overflow: 'hidden',
                                      mb: 2,
                                      '& a': { color: 'primary.main' },
                                      '& ul, & ol': { pl: 2 },
                                      '& p': { my: 0.5 },
                                      '& img': { maxWidth: '100%', height: 'auto' }
                                    }}
                                  >
                                    <ReactMarkdown>{event.content}</ReactMarkdown>
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          </motion.div>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))}
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Have an event to suggest?
          </Typography>
          <Typography variant="body1" paragraph>
            If you'd like to suggest a community event, please contact the Social Committee.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<EventIcon />}
            component="a"
            href="/contacts"
          >
            Contact Social Committee
          </Button>
        </Box>
      </Container>
    </>
  );
};

export default Events; 